import type { Identity, ShellAuth } from '@thecvlb/auth-web';
import type { Persistor } from 'redux-persist';
import { RootState, store } from 'store';
import { clearUser, setLoginServiceSession } from 'store/user/userSlice';

import { sessionKeyOfIdentity } from './loginService';

/** How long the first paint waits for the login service to restore a session. */
const RESTORE_TIMEOUT_MS = 5_000;

const savedUser = () => (store.getState() as RootState).user;

const heldByLoginService = () => !!savedUser().authSessionKey;

/** A Cognito session: a token the login service did not install. */
const heldByCognito = () => {
  const { accessToken, authSessionKey } = savedUser();
  return !!accessToken && !authSessionKey;
};

/**
 * Whether the saved profile, if any, belongs to someone other than this identity. Compared by
 * email: a staff subject is the login service's identity id, never the profile's `_id` (LFA-246),
 * and the backend resolves staff by that email. An identity without an email is not the same person.
 */
const savedProfileIsSomeoneElse = (identity: Identity) => {
  const { _id, email } = savedUser();

  if (!_id) {
    return false;
  }

  return (
    identity.kind !== 'staff' ||
    !identity.email ||
    !email ||
    identity.email.toLowerCase() !== email.toLowerCase()
  );
};

/**
 * Mirrors who is signed in at the login service into the store (LMD-683). A signed-in identity
 * installs its session and the usual post-sign-in steps run; signing out clears the user. Only
 * sessions the login service installed are cleared here, a Cognito session is never replaced (it
 * stays until sign-out or idle logout), and another person's saved profile is cleared first, as on
 * `identity-changed`.
 */
const sync = (identity: Identity | null) => {
  if (!identity) {
    if (heldByLoginService()) {
      store.dispatch(clearUser());
    }
    return;
  }

  if (heldByCognito()) {
    return;
  }

  if (savedProfileIsSomeoneElse(identity)) {
    store.dispatch(clearUser());
  }
  store.dispatch(
    setLoginServiceSession({
      sessionKey: sessionKeyOfIdentity(identity),
      source: identity.authSource
    })
  );
};

/**
 * Resolves once redux-persist has rehydrated. A login-service session is never saved, so one
 * restored earlier would be overwritten by rehydration.
 */
export const waitForRehydration = (persistor: Persistor) =>
  new Promise<void>((resolve) => {
    if (persistor.getState().bootstrapped) {
      resolve();
      return;
    }

    const unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) {
        unsubscribe();
        resolve();
      }
    });
  });

/**
 * Installs the session the login service already holds, as on the first render. For a sign-in that
 * resolves with nothing mounted because the service is already signed in, e.g. after a Cognito
 * session it was kept behind has signed out. Resolves, never rejects.
 */
export const restoreLoginServiceSession = (auth: ShellAuth): Promise<void> =>
  auth
    .getSession()
    .then((session) => sync(session.user))
    .catch(() => {});

/**
 * Subscribes to the login service and restores any session it already holds. Resolves, never rejects, once the
 * restore settles, fails or times out, so the first render knows whether the staff member is signed in and
 * the route guards do not send a signed-in user to the sign-in page.
 */
export const connectLoginService = async (auth: ShellAuth, timeoutMs = RESTORE_TIMEOUT_MS) => {
  auth.onChange((event, identity) => {
    // A renewal does not change who is signed in.
    if (event === 'token-refreshed') {
      return;
    }
    // Another person: drop everything held for the previous one before installing the new session.
    if (event === 'identity-changed') {
      store.dispatch(clearUser());
    }
    sync(identity);
  });

  // Bounded: a failure or hang counts as no session; a late result may still install it.
  const restore = restoreLoginServiceSession(auth);
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));

  await Promise.race([restore, timeout]);
};
